import { Col, Row, Tooltip, OverlayTrigger } from "react-bootstrap";
import { useSiteContext } from "./Providers/SSCProvider";
import { useMapContext } from "./Providers/MapProvider";
import { useModalContext } from "./Providers/ModalProvider";

import { FlagIcon } from "react-flag-kit";
import countries from "i18n-iso-countries";
import Icon from "../generic/Icon";
import { useMemo } from "react";

const SiteInfo = () => {
  const {
    formatBandwidth,
    formatCost,
    calculateSitePortBandwidthBreakdown,
    calculateSiteMrcCostBreakdown,
  } = useMapContext();
  const { selectedSite, selectedStructure, selectedCircuit } = useSiteContext();
  const { openModal } = useModalContext();

  const selectedCarrierInfo = false;
  const isAdmin = true;

  const formatBandwidthTooltip = (breakdown) => {
    const currentFormatted = formatBandwidth(breakdown.current);
    const futureFormatted = formatBandwidth(breakdown.future);

    return (
      <div>
        <div>Current: {currentFormatted}</div>
        <div>Future: {futureFormatted}</div>
      </div>
    );
  };

  const formatCostTooltip = (breakdown) => {
    const currentFormatted = formatCost(breakdown.current);
    const futureFormatted = formatCost(breakdown.future);

    return (
      <div>
        <div>Current: {currentFormatted}</div>
        <div>Future: {futureFormatted}</div>
      </div>
    );
  };

  const bandwidthBreakdown = useMemo(
    () => calculateSitePortBandwidthBreakdown(selectedSite),
    [selectedSite, calculateSitePortBandwidthBreakdown],
  );

  const costBreakdown = useMemo(
    () => calculateSiteMrcCostBreakdown(selectedSite),
    [selectedSite, calculateSiteMrcCostBreakdown],
  );

  return (
    <>
      <Row className="p-2">
        <Col className="d-flex justify-content-start align-items-center">
          <div
            className="d-flex align-items-center gap-3 flex-wrap"
            style={{ position: "relative", width: "100%" }}
          >
            <div
              className="d-flex gap-3 flex-wrap"
              style={{ width: "100%", alignItems: "baseline" }}
            >
              <div
                className="d-flex align-items-center gap-3 flex-wrap"
                style={{ minWidth: 0, flex: 1 }}
              >
                {selectedSite?.countryCode ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-country">
                        {selectedSite.country}
                      </Tooltip>
                    }
                  >
                    <div>
                      <FlagIcon
                        code={countries.alpha3ToAlpha2(
                          selectedSite.countryCode,
                        )}
                        style={{ width: "25px", height: "auto" }}
                      />
                    </div>
                  </OverlayTrigger>
                ) : (
                  <div className="pt-1">
                    <span style={{ fontWeight: "bold" }}>
                      {selectedSite?.country}
                    </span>
                  </div>
                )}
                <div className="pt-1">
                  <span style={{ fontWeight: "bold" }}>
                    {selectedSite?.type}
                  </span>
                </div>
                <Icon
                  name={"location"}
                  onClick={() =>
                    openModal("SITE_DATA", {
                      siteData: selectedSite,
                    })
                  }
                  overrides={{
                    tooltip: "View Site Details",
                    tooltipPlacement: "top",
                  }}
                  id="info-icon"
                />
                <span
                  style={{
                    fontSize: "0.9rem",
                    wordBreak: "break-word",
                    minWidth: 0,
                    maxWidth: "100%",
                    display: "inline-block",
                  }}
                >
                  {[
                    selectedSite?.addressOne,
                    selectedSite?.addressTwo,
                    selectedSite?.city,
                    selectedSite?.state,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </span>

                {selectedStructure && (
                  <>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-country">
                          Selected Structure
                        </Tooltip>
                      }
                    >
                      <span
                        style={{
                          fontSize: "0.9rem",
                          wordBreak: "break-word",
                          minWidth: 0,
                          maxWidth: "100%",
                          display: "inline-block",
                        }}
                      >
                        <span style={{ fontWeight: "bold" }}>
                          Selected Structure:
                        </span>{" "}
                        {[
                          selectedStructure?.structureId,
                          selectedStructure?.structureName,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </OverlayTrigger>
                  </>
                )}

                {selectedCircuit && (
                  <>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-country">Selected Circuit</Tooltip>
                      }
                    >
                      <span
                        style={{
                          fontSize: "0.9rem",
                          wordBreak: "break-word",
                          minWidth: 0,
                          maxWidth: "100%",
                          display: "inline-block",
                        }}
                      >
                        <span style={{ fontWeight: "bold" }}>
                          Selected Circuit:
                        </span>{" "}
                        {selectedCircuit?.circuitVendorId}
                      </span>
                    </OverlayTrigger>
                  </>
                )}
              </div>
              {!selectedCarrierInfo && (
                <div
                  className="mt-1 d-flex align-items-center gap-3 flex-wrap"
                  style={{
                    minWidth: 0,
                    flexShrink: 0,
                    marginTop: "-40px",
                  }}
                >
                  <div className="d-flex align-items-center gap-1">
                    <Icon
                      name={"bandwidth"}
                      overrides={{
                        tooltip: formatBandwidthTooltip(bandwidthBreakdown),
                        tooltipPlacement: "auto",
                      }}
                      id="bandwidth-icon"
                    />
                    <span style={{ fontSize: "0.85rem", marginTop: "5px" }}>
                      {formatBandwidth(bandwidthBreakdown.total)}
                    </span>
                  </div>
                  {isAdmin && (
                    <div className="d-flex align-items-center gap-1">
                      <Icon
                        name={"cost"}
                        overrides={{
                          tooltip: formatCostTooltip(costBreakdown),
                          tooltipPlacement: "auto",
                        }}
                        id="cost-icon"
                      />
                      <span style={{ fontSize: "0.85rem", marginTop: "5px" }}>
                        {formatCost(costBreakdown.total)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default SiteInfo;
